/**
 * E-Invoicing Configuration
 * Manages settings for invoice generation and delivery via Billit, UBL email, and future channels.
 */

/**
 * Supported e-invoicing delivery channels
 */
export enum EInvoicingChannel {
  BILLIT = 'BILLIT', // Send via Billit API (Peppol, email, post)
  EMAIL_UBL = 'EMAIL_UBL', // Send UBL XML as email attachment
  DISABLED = 'DISABLED', // E-invoicing disabled
}

/**
 * Billit-specific configuration. Billit knows the seller (the company of the API key).
 */
export interface BillitConfig {
  apiKey?: string; // Billit API key (can be stored encrypted or in env)
  companyId?: string; // Billit party ID of the company
  defaultLanguage?: string; // Default invoice language (e.g., 'nl', 'fr', 'en')
  sendToPeppol?: boolean; // Send the invoice on the Peppol network (otherwise only created in Billit)
}

/**
 * Email UBL configuration
 */
export interface EmailUBLConfig {
  attachPdf?: boolean; // Also attach a PDF version
  toEmail?: string;
  ccEmails?: string[]; // CC recipients
}

/**
 * Custom numbering configuration for e-invoicing.
 */
export interface CustomNumberingConfig {
  enabled: boolean;
  prefix: string; // supports {year}, {month}, {day} tokens
  creditNotePrefix: string;
}

/**
 * Main E-Invoicing Configuration Class
 * Only one channel can be active at a time.
 */
export class EInvoicingConfig {
  // Active channel (only one at a time)
  channel: EInvoicingChannel = EInvoicingChannel.DISABLED;

  // Manual processing mode: if true, invoices must be manually triggered
  // If false, invoices are automatically processed via the queue
  manualProcessing?: boolean = false;

  // Channel-specific configurations (used when channel matches)
  billit: BillitConfig = {
    defaultLanguage: 'fr',
    sendToPeppol: true,
  };

  emailUbl: EmailUBLConfig = {
    attachPdf: true,
  };

  // Custom numbering for e-invoicing
  customNumbering: CustomNumberingConfig = {
    enabled: false,
    prefix: 'EINV/{year}/',
    creditNotePrefix: 'ECNV/{year}/',
  };

  constructor(partial?: Partial<EInvoicingConfig>) {
    if (partial) {
      // Save nested defaults before Object.assign overwrites them
      const defaultBillit = { ...this.billit };
      const defaultEmailUbl = { ...this.emailUbl };
      const defaultCustomNumbering = { ...this.customNumbering };

      Object.assign(this, partial);
      // The seller is the shop (legal and bank information of the shop configuration)
      delete (this as any).sellerDefaults;

      // Deep merge nested objects, restoring defaults if partial had undefined
      this.billit = { ...defaultBillit, ...(partial.billit || {}) };
      this.emailUbl = { ...defaultEmailUbl, ...(partial.emailUbl || {}) };
      this.customNumbering = { ...defaultCustomNumbering, ...(partial.customNumbering || {}) };
    }
  }

  /**
   * Check if e-invoicing is enabled
   */
  isEnabled(): boolean {
    return this.channel !== EInvoicingChannel.DISABLED;
  }
}
